/**
 * Shared utilities for DatePicker components.
 * Handles UTC↔local conversion for date-time and time variants.
 * Range mode: values stored as "start/end" where each uses the variant's format.
 */

export type DatePickerVariant = 'date' | 'time' | 'date-time' | 'month' | 'year';

/** Get the user's timezone abbreviation (e.g., "EDT", "PST") */
export function getTimezoneAbbr(): string {
  return Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find(p => p.type === 'timeZoneName')?.value ?? '';
}

/** Map HTML input type for each variant */
export function getInputType(variant: DatePickerVariant): string {
  switch (variant) {
    case 'date': return 'date';
    case 'time': return 'time';
    case 'date-time': return 'datetime-local';
    case 'month': return 'month';
    case 'year': return 'number';
  }
}

/**
 * Convert stored UTC value to local value for display in inputs.
 * date, month, year are timezone-agnostic (pass through).
 * date-time and time are stored as UTC and need local conversion.
 */
export function utcToLocal(value: string, variant: DatePickerVariant): string {
  if (!value) return '';

  switch (variant) {
    case 'date':
    case 'month':
    case 'year':
      return value;
    case 'date-time': {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return formatLocalDatetime(d);
    }
    case 'time': {
      const d = new Date(`1970-01-01T${value}`);
      if (isNaN(d.getTime())) return value;
      return formatLocalTime(d);
    }
  }
}

/**
 * Convert local input value to UTC for storage.
 */
export function localToUtc(value: string, variant: DatePickerVariant): string {
  if (!value) return '';

  switch (variant) {
    case 'date':
    case 'month':
    case 'year':
      return value;
    case 'date-time': {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
    }
    case 'time': {
      const [h, m] = value.split(':').map(Number);
      const d = new Date(1970, 0, 1, h, m, 0);
      return pad(d.getUTCHours()) + ':' + pad(d.getUTCMinutes()) + ':00Z';
    }
  }
}

/** Format a Date as local "YYYY-MM-DDTHH:mm" for datetime-local input */
function formatLocalDatetime(d: Date): string {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
    'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
}

/** Format a Date as local "HH:mm" for time input */
function formatLocalTime(d: Date): string {
  return pad(d.getHours()) + ':' + pad(d.getMinutes());
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Format a single stored value for display.
 */
export function formatDisplaySingle(value: string, variant: DatePickerVariant): string {
  if (!value) return '';

  switch (variant) {
    case 'date': {
      const d = new Date(value + 'T00:00:00');
      return isNaN(d.getTime()) ? value : d.toLocaleDateString();
    }
    case 'time': {
      const d = new Date(`1970-01-01T${value}`);
      return isNaN(d.getTime()) ? value : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    case 'date-time': {
      const d = new Date(value);
      return isNaN(d.getTime()) ? value : d.toLocaleString();
    }
    case 'month': {
      const [y, m] = value.split('-').map(Number);
      const d = new Date(y, m - 1);
      return isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    }
    case 'year':
      return value;
  }
}

/**
 * Format stored value for display, handling both single and range.
 */
export function formatDisplay(value: string, variant: DatePickerVariant, range?: boolean): string {
  if (!value) return '';
  if (range) {
    const [start, end] = value.split('/');
    const s = formatDisplaySingle(start, variant);
    const e = formatDisplaySingle(end, variant);
    return s && e ? `${s} – ${e}` : s || e || value;
  }
  return formatDisplaySingle(value, variant);
}

/**
 * Parse a stored date string to a Date object for calendar pickers.
 */
export function parseToDate(value: string, variant: DatePickerVariant): Date | undefined {
  if (!value) return undefined;
  switch (variant) {
    case 'date':
    case 'month':
      return parseDateLocal(value);
    case 'date-time':
    case 'time': {
      const v = variant === 'time' ? `1970-01-01T${value}` : value;
      const d = new Date(v);
      return isNaN(d.getTime()) ? undefined : d;
    }
    case 'year': {
      const d = new Date(Number(value), 0);
      return isNaN(d.getTime()) ? undefined : d;
    }
  }
}

/** Parse a date range value "start/end" to { from, to } */
export function parseDateRange(value: string): { from: Date; to: Date } | undefined {
  if (!value) return undefined;
  const [start, end] = value.split('/');
  const from = parseDateLocal(start);
  const to = parseDateLocal(end);
  if (!from || !to) return undefined;
  return { from, to };
}

/** Parse "YYYY-MM-DD" or "YYYY-MM" as a local date (not UTC) */
function parseDateLocal(value: string): Date | undefined {
  if (!value) return undefined;
  const parts = value.split('-').map(Number);
  const d = parts.length === 2
    ? new Date(parts[0], parts[1] - 1)
    : new Date(parts[0], parts[1] - 1, parts[2]);
  return isNaN(d.getTime()) ? undefined : d;
}

/** Format a local Date as "YYYY-MM-DD" */
export function formatDateISO(d: Date): string {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

/** Format a local Date as "YYYY-MM" */
export function formatMonthISO(d: Date): string {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1);
}

// ── min/max helpers ──

/** Parse a min/max prop (string or Date) to a Date object */
export function parseMinMax(value: string | Date | undefined): Date | undefined {
  if (value === undefined || value === '') return undefined;
  if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
  // Try parsing as ISO string
  const d = new Date(value.includes('T') || value.includes('Z') ? value : value + 'T00:00:00');
  return isNaN(d.getTime()) ? undefined : d;
}

/**
 * Build react-day-picker disabled matchers from min/max.
 * Returns an array of Matcher objects: { before: minDate } and/or { after: maxDate }.
 */
export function buildDisabledDays(min?: string | Date, max?: string | Date): Array<{ before: Date } | { after: Date }> {
  const result: Array<{ before: Date } | { after: Date }> = [];
  const minDate = parseMinMax(min);
  const maxDate = parseMinMax(max);
  if (minDate) result.push({ before: minDate });
  if (maxDate) result.push({ after: maxDate });
  return result;
}

/** Check if a month value "YYYY-MM" is within min/max range (inclusive) */
export function isMonthInRange(monthVal: string, min?: string, max?: string): boolean {
  if (!min && !max) return true;
  // Compare as strings since YYYY-MM sorts lexicographically
  if (min && monthVal < min.slice(0, 7)) return false;
  if (max && monthVal > max.slice(0, 7)) return false;
  return true;
}

/** Check if a year value "YYYY" is within min/max range (inclusive) */
export function isYearInRange(yearVal: string, min?: string, max?: string): boolean {
  if (!min && !max) return true;
  const y = Number(yearVal);
  if (min && y < Number(min.slice(0, 4))) return false;
  if (max && y > Number(max.slice(0, 4))) return false;
  return true;
}

/** Get startMonth for react-day-picker navigation limiting */
export function getStartMonth(min?: string | Date): Date | undefined {
  return parseMinMax(min);
}

/** Get endMonth for react-day-picker navigation limiting */
export function getEndMonth(max?: string | Date): Date | undefined {
  return parseMinMax(max);
}

/** Convert min/max to local time string for HTML time input */
export function minMaxToLocalTime(value: string | Date | undefined): string | undefined {
  if (value === undefined) return undefined;
  const str = typeof value === 'string' ? value : value.toISOString();
  // If it's a time string like "14:30:00Z", convert to local
  if (/^\d{2}:\d{2}/.test(str)) return utcToLocal(str, 'time');
  return undefined;
}

/** Month names for month picker grid */
export const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Get the decade start for year picker (e.g., 2026 → 2020) */
export function getDecadeStart(year: number): number {
  return Math.floor(year / 12) * 12;
}
